import { State } from '../State';
import {
  Match2BuruStreakRewardSet,
  Match2PowerupType,
  match2BuruStreakRewards,
  match2Continues,
  match2MaxLevel,
  match2UnlockableLevels,
  getMatch2PlayOnAppCooldownTimestampKey,
  match2Scoring,
  freeExtraMovesUsedPerContinue,
  match2BoosterCatalog,
  match2PowerupCatalog,
  Match2ScoringConfig,
} from './match2.ruleset';
import {
  seasonalBackgroundSchedule,
  Match2BackgroundMetadata,
} from './match2.background.ruleset';
import { getCurrentGachaEvent } from '../gacha/gacha.getters';
import { getCurrentLevel } from '../level/level.getters';
import { decorationUnlockLevel } from '../decoration/decoration.ruleset';
import { BoosterId } from 'src/game/minigame/match2-timestep/defs/booster';
import {
  getMatch2TimedRewardTimestampKey,
  getProcessedMatch2Rewards,
} from './rewards/match2-rewards.getters';
import {
  Match2Reward,
  Match2TimedRewardType,
  isRandomReward,
  isTimedReward,
} from './rewards/match2-rewards.ruleset';
import { canPlayLeague, getLeagueLevel } from './leagues/leagues.getters';
import { getEnergyGemValue } from '../inventory/inventory.getters';
import { assertNever } from '../utils';
import {
  puzzleCoinsGetReward,
  puzzleCoinsIsPlayingTutorialDeterministic,
} from '../puzzlecoins/puzzlecoins.getters';
import { isMatch2FrenzyUnlocked } from './frenzy/match2-frenzy.getters';
import {
  getStreakBattleRoyaleActiveTheme,
  getStreakBattleRoyaleStatus,
} from '../streakbattleroyale/streakbattleroyale.getters';
import { cardsGetLevelWinRewards } from '../cards/cards.getters';

export function isMatch2DailyBonusActive(state: State): boolean {
  // Show daily bonus after completing the basic gameplay tutorial.
  return state.tutorial.basicGameplayShown;
}

/**
 * Get the remaining cooldown for the daily bonus.
 * @returns Remaining cooldown in milliseconds.
 */
export function getDailyBonusRemainingCooldown(
  state: State,
  now: number,
): number {
  return Math.max(0, state.match2.dailyBonus.nextBonusTimestamp - now);
}

export function getDailyBonusRewards(state: State): Match2Reward[] {
  const { nextBonusTimestamp } = state.match2.dailyBonus;
  const id = 'dailyBonusRewardNoBPointsSingle';
  return getProcessedMatch2Rewards(id, nextBonusTimestamp);
}

export function getMatch2LevelRewards(
  state: State,
  result: 'win' | 'lose',
  stars: number,
  now: number,
  args?: { hasContinued?: boolean },
): Match2Reward[] {
  if (result === 'lose') return [];
  const rewards: Match2Reward[] = [];

  if (
    state.match2.pendingRaceScore &&
    state.match2.pendingRaceScore.score > 0
  ) {
    rewards.push({
      type: 'feature',
      subType: 'race_points',
      raceId: state.match2.pendingRaceScore.raceId,
      amount: state.match2.pendingRaceScore.score,
    });
  }

  const reward = puzzleCoinsGetReward(state, stars);
  if (reward) rewards.push(reward);

  rewards.push(
    ...cardsGetLevelWinRewards(state, {
      now,
      winStreak: state.match2.winStreak + 1,
      hasContinued: args?.hasContinued,
    }),
  );

  return rewards;
}

export function hasExtraMovesInInventory(state: State): boolean {
  return state.match2.inventory.extraMoves > 0;
}

/**
 * Get the number of moves that will be added to game.
 * if the remaining extra moves in the inventory is less than the defined amount per free continue, return the remaining amount instead.
 */
export function getExtraMovesFromFreeContinue(state: State): number {
  return Math.min(
    state.match2.inventory.extraMoves,
    freeExtraMovesUsedPerContinue,
  );
}

export function getMatch2ContinueConfig(state: State):
  | { moves: number; price: number; powerups?: undefined }
  | {
      moves: number;
      powerups: { type: string; amount: number }[];
      price: number;
    } {
  const continues = state.match2.continues;
  const config =
    match2Continues[Math.min(continues, match2Continues.length - 1)];
  return config;
}
export function getCurrentBuruStreakRewardSet(
  state: State,
): Match2BuruStreakRewardSet | undefined {
  const streak = state.match2.buruStreak.value;
  let rewardSet: Match2BuruStreakRewardSet | undefined;
  match2BuruStreakRewards.forEach((rs) => {
    if (streak >= rs.wins) {
      rewardSet = rs;
    }
  });
  return rewardSet;
}

export function getBuruStreakPowerups(
  state: State,
): ('rocket' | 'bomb' | 'cube')[] {
  let rewardSet = getCurrentBuruStreakRewardSet(state);
  const powerups: Match2PowerupType[] = [];

  if (!rewardSet) return powerups;
  for (const key in rewardSet.rewards) {
    const amount = rewardSet.rewards[key as Match2PowerupType];
    powerups.push(...new Array(amount).fill(key));
  }

  return powerups;
}

export function isMatch2UnlockableUnlocked(
  state: State,
  item: Match2PowerupType | BoosterId | 'buruStreak' | 'frenzy',
): boolean {
  return state.match2.level >= getMatch2UnlockableLevels(item);
}

export function getMatch2UnlockableLevels(
  item: Match2PowerupType | BoosterId | 'buruStreak' | 'frenzy',
): number {
  return match2UnlockableLevels[item];
}

/**
 * rerieve cooldown timestamp by key, if not exist return 0
 */
export function getMatch2Timestamp(state: State, key: string): number {
  return state.match2?.timestamps?.[key] ?? 0;
}

/**
 * Check if a frenzy reward is currently active.
 */
export function isMatch2TimedRewardActive(
  state: State,
  type: Match2TimedRewardType,
  now: number,
): boolean {
  // Timed energy should always be active
  if (type !== 'energy' && !isMatch2FrenzyUnlocked(state)) return false;
  const key = getMatch2TimedRewardTimestampKey(type);
  return getMatch2Timestamp(state, key) > now;
}

/**
 * Get the remaining time for a frenzy reward, in milliseconds.
 */
export function getMatch2TimedRewardRemainingTime(
  state: State,
  type: Match2TimedRewardType,
  now: number,
): number {
  // Timed energy should always return the time, since it's always active
  if (type !== 'energy' && !isMatch2FrenzyUnlocked(state)) return 0;
  const key = getMatch2TimedRewardTimestampKey(type);
  return Math.max(getMatch2Timestamp(state, key) - now, 0);
}

/**
 * Get the timestamp of the last time the user saw the frenzy popup.
 */
export function canShowMatch2PlayOnAppPopup(
  state: State,
  now: number,
  sessionNonSkipGamesPlayed: number,
): boolean {
  return (
    state.match2.level > 5 &&
    sessionNonSkipGamesPlayed >= 3 &&
    now >= getMatch2Timestamp(state, getMatch2PlayOnAppCooldownTimestampKey())
  );
}

/**
 * Get the scoring configuration
 */
export function getScoringConfig(state: State): Match2ScoringConfig {
  return match2Scoring;
}

/**
 * Get base score for a match2 game,
 * @param scoringConfig the scoring configuration to be used for the calculation
 * @param moves number of moves
 * @returns base score
 */
export function getBaseScore(
  scoringConfig: Match2ScoringConfig,
  moves: number,
): number {
  return (
    Math.round(moves * scoringConfig.baseScoreFactor) *
    scoringConfig.blockDestroyed
  );
}

/**
 * Get current match2 level.
 * @param state
 * @returns
 */
export function getMatch2Level(state: State, useLeague = false): number {
  return canPlayLeague(state) && useLeague
    ? getLeagueLevel(state)
    : Math.min(state.match2?.level ?? 1, getMatch2MaxLevel(state));
}

/**
 * Get gem value for a match2 reward.
 * @param reward
 * @returns
 */
export function getMatch2RewardGemValue(
  state: State,
  reward: Match2Reward[],
): number {
  const result = reward.reduce((acc, cur) => {
    if (isRandomReward(cur)) return acc;
    const type = cur.type;
    switch (type) {
      case 'gems':
        return acc + cur.amount;

      case 'boosters': {
        if (isTimedReward(cur)) return acc + 200;
        else {
          const { price, quantity } = match2BoosterCatalog[cur.subType];
          return acc + (price / quantity) * cur.amount;
        }
      }

      case 'powerups': {
        if (isTimedReward(cur)) return acc + 200;
        else {
          const { price, quantity } = match2PowerupCatalog[cur.subType];
          return acc + (price / quantity) * cur.amount;
        }
      }

      case 'energy':
        if (isTimedReward(cur)) return acc + 200;
        else return acc + getEnergyGemValue(state, cur.amount);

      case 'card':
        return acc;

      // Not handled yet.
      case 'bPoints':
      case 'feature':
      case 'moves':
      case 'multiplier':
      case 'reward_set':
      case 'wrapper':
      case 'roomDecoration':
      case 'cardPack':
        // Throw error in development.
        if (process.env.IS_DEVELOPMENT) {
          throw new Error(`Cannot get gem value of reward type: ${cur.type}`);
        }
        return acc;

      default:
        assertNever(type);
    }
  }, 0);

  return Math.floor(result);
}

/**
 * Get max level for a match2 game.
 * @param state
 */
export function getMatch2MaxLevel(_: State): number {
  return match2MaxLevel;
}

/**
 * Return whether the match2 puzzle should spend energy.
 * @param state
 * @param args
 * @returns
 */
export function shouldMatch2SpendEnergy(
  state: State,
  args: { now: number; level?: number },
): boolean {
  const level = args.level ?? state.match2.level;

  // If playing the tutorial, don't spend energy during the tutorial levels.
  if (puzzleCoinsIsPlayingTutorialDeterministic(state)) {
    return false;
  }

  // If the bpoints-from-puzzle feature is enabled and the user is on level 1, don't spend energy.
  if (state.match2.level === 1) {
    return false;
  }

  // If user has a timed infinite energy, don't spend energy.
  if (isMatch2TimedRewardActive(state, 'energy', args.now)) {
    return false;
  }

  // Match2 puzzle should spend energy by default
  return true;
}

export function isMatch2Available(state: State): boolean {
  // Minigames are always available since we resolved bpoints-from-puzzle to enabled
  return true;
}

/**
 * Get current puzzle background metadata including image and effect.
 * Priority: Fuji Quest > Gacha > Seasonal > Default
 *
 * @param state - Current game state
 * @param now - Current timestamp in milliseconds
 * @returns Background metadata with image filename and optional effect
 */
export function getMatch2BackgroundMetadata(
  state: State,
  now: number,
): Match2BackgroundMetadata {
  const defaultBackground: Match2BackgroundMetadata = { image: 'BG.png' };

  // Priority 1: Fuji Quest (streakBattleRoyale lightning theme)
  if (
    getStreakBattleRoyaleStatus(state) === 'active' &&
    getStreakBattleRoyaleActiveTheme(state) === 'lightning'
  ) {
    return { image: 'BG_fujiquestv2.ver.png' };
  }

  // Priority 2: Gacha event background (only if user has access to gacha)
  if (getCurrentLevel(state) >= decorationUnlockLevel) {
    const currentGacha = getCurrentGachaEvent(state, now);
    if (currentGacha?.metadata?.puzzleBackground) {
      return {
        image: currentGacha.metadata.puzzleBackground,
        effect: currentGacha.metadata.puzzleBackgroundEffect,
      };
    }
  }

  // Priority 3: Seasonal background
  const seasonalEvent = seasonalBackgroundSchedule.getCurrentEvent(now);
  if (seasonalEvent?.metadata) {
    return seasonalEvent.metadata;
  }

  // Priority 4: Default background
  return defaultBackground;
}
